import { z } from 'zod';
import { PersonRefSchema, SiteRefSchema } from './property';

/**
 * Fleet (docs/specs/fleet.md, D63/D64). M1: the register with vehicle dates (Aroo), and a live
 * read model of dt_spar's licence checks, consents, Quartix scores, fines, repairs, incidents,
 * checklists and fuel cards. Dates are YYYY-MM-DD; dt_spar times are Europe/London wall clock
 * "YYYY-MM-DDTHH:MM:SS"; Aroo instants are ISO. Money is a decimal string.
 */

const IsoDate = z.iso.date();
const Instant = z.iso.datetime();
/** Europe/London wall clock from dt_spar, "2026-10-08T11:50:37". */
const Wall = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/);
const Money = z.string().regex(/^-?\d+(\.\d{1,2})?$/, 'Use pounds and pence, e.g. 0.75');

export const FLEET_PERMISSIONS = {
  vehiclesView: 'fleet.vehicles.view',
  driversView: 'fleet.drivers.view',
  manage: 'fleet.admin.manage',
  ownView: 'fleet.own.view',
} as const;

// Vocabularies ---------------------------------------------------------------------

export const VehicleDateKindSchema = z.enum(['mot', 'tax', 'insurance', 'service']);
export type VehicleDateKind = z.infer<typeof VehicleDateKindSchema>;
export const VEHICLE_DATE_KIND_LABELS: Record<VehicleDateKind, string> = {
  mot: 'MOT',
  tax: 'Tax',
  insurance: 'Insurance',
  service: 'Service',
};

export const VehicleDateSourceSchema = z.enum(['dvsa', 'dvla', 'quartix', 'manual', 'import']);
export type VehicleDateSource = z.infer<typeof VehicleDateSourceSchema>;
export const VEHICLE_DATE_SOURCE_LABELS: Record<VehicleDateSource, string> = {
  dvsa: 'DVSA (nightly)',
  dvla: 'DVLA (nightly)',
  quartix: 'Quartix',
  manual: 'Entered in Aroo',
  import: 'Imported',
};

export const VehicleDateStatusSchema = z.enum(['ok', 'due_soon', 'overdue', 'missing']);
export type VehicleDateStatus = z.infer<typeof VehicleDateStatusSchema>;
export const VEHICLE_DATE_STATUS_LABELS: Record<VehicleDateStatus, string> = {
  ok: 'OK',
  due_soon: 'Due soon',
  overdue: 'Overdue',
  missing: 'Not recorded',
};

export const VehicleStatusSchema = z.enum(['active', 'vor', 'disposed']);
export type VehicleStatus = z.infer<typeof VehicleStatusSchema>;
export const VEHICLE_STATUS_LABELS: Record<VehicleStatus, string> = {
  active: 'Active',
  vor: 'Off road',
  disposed: 'Disposed',
};

export const OwnershipSchema = z.enum(['owned', 'leased', 'grey']);
export type Ownership = z.infer<typeof OwnershipSchema>;
export const OWNERSHIP_LABELS: Record<Ownership, string> = {
  owned: 'Owned',
  leased: 'Leased',
  grey: 'Own car (grey fleet)',
};

export const VehicleTypeSchema = z.enum(['van', 'car', 'other']);

export const VEHICLE_TYPE_LABELS: Record<z.infer<typeof VehicleTypeSchema>, string> = {
  van: 'Van',
  car: 'Car',
  other: 'Other',
};
export type VehicleType = z.infer<typeof VehicleTypeSchema>;

export const LicenceStatusSchema = z.enum(['ok', 'due_soon', 'overdue', 'never_checked', 'exempt']);
export type LicenceStatus = z.infer<typeof LicenceStatusSchema>;
export const LICENCE_STATUS_LABELS: Record<LicenceStatus, string> = {
  ok: 'Checked',
  due_soon: 'Check due soon',
  overdue: 'Check overdue',
  never_checked: 'Never checked',
  exempt: 'Exempt',
};

export const ConsentStatusSchema = z.enum(['valid', 'expiring', 'expired', 'missing']);
export type ConsentStatus = z.infer<typeof ConsentStatusSchema>;
export const CONSENT_STATUS_LABELS: Record<ConsentStatus, string> = {
  valid: 'Valid',
  expiring: 'Expiring',
  expired: 'Re-consent due',
  missing: 'No consent',
};

export const LicenceBandSchema = z.object({
  minPoints: z.number().int().min(0).max(99),
  months: z.number().int().min(1).max(60),
});
export type LicenceBand = z.infer<typeof LicenceBandSchema>;

// Pieces -----------------------------------------------------------------------------

export const VehicleRefSchema = z.object({ id: z.uuid(), reg: z.string() });
export type VehicleRef = z.infer<typeof VehicleRefSchema>;

export const VehicleDateCellSchema = z.object({
  dueDate: IsoDate.nullable(),
  status: VehicleDateStatusSchema,
  daysLeft: z.number().int().nullable(),
});
export type VehicleDateCell = z.infer<typeof VehicleDateCellSchema>;

export const VehicleDateRecordSchema = z.object({
  id: z.uuid(),
  kind: VehicleDateKindSchema,
  dueDate: IsoDate.nullable(),
  source: VehicleDateSourceSchema,
  note: z.string().nullable(),
  recordedAt: Instant,
  recordedBy: PersonRefSchema.nullable(),
  current: z.boolean(),
});
export type VehicleDateRecord = z.infer<typeof VehicleDateRecordSchema>;

export const FleetFineSchema = z.object({
  /** M2: the Aroo case id (fleet_cases). */
  id: z.uuid(),
  ref: z.string().nullable(),
  type: z.string().nullable(),
  reg: z.string().nullable(),
  status: z.string().nullable(),
  open: z.boolean(),
  issueDate: IsoDate.nullable(),
  dueDate: IsoDate.nullable(),
  points: z.number().int().nullable(),
  /** Cost viewers only (D65: fleet admin, directors, the branch's BM/RM); null otherwise. */
  amount: Money.nullable(),
  driver: PersonRefSchema.nullable(),
});
export type FleetFine = z.infer<typeof FleetFineSchema>;

export const FleetRepairSchema = z.object({
  id: z.uuid(),
  poNumber: z.string().nullable(),
  reg: z.string().nullable(),
  status: z.string().nullable(),
  open: z.boolean(),
  onHold: z.boolean(),
  priority: z.string().nullable(),
  raisedAt: Wall.nullable(),
  completedAt: Wall.nullable(),
  /** Cost viewers only (D65). */
  totalCost: Money.nullable(),
});
export type FleetRepair = z.infer<typeof FleetRepairSchema>;

export const FleetIncidentSchema = z.object({
  id: z.uuid(),
  caseNumber: z.string().nullable(),
  occurredAt: Wall.nullable(),
  reg: z.string().nullable(),
  type: z.string().nullable(),
  severity: z.string().nullable(),
  status: z.string().nullable(),
  open: z.boolean(),
  driver: PersonRefSchema.nullable(),
});
export type FleetIncident = z.infer<typeof FleetIncidentSchema>;

export const FleetChecklistSchema = z.object({
  kind: z.enum(['weekly', 'daily']),
  completedAt: Wall,
  by: PersonRefSchema.nullable(),
  mileage: z.number().int().nullable(),
  failedItems: z.array(z.string()),
});
export type FleetChecklist = z.infer<typeof FleetChecklistSchema>;

export const FleetFuelCardSchema = z.object({
  id: z.number().int(),
  last4: z.string().nullable(),
  status: z.string().nullable(),
  product: z.string().nullable(),
  expiresOn: IsoDate.nullable(),
  lastUsedOn: IsoDate.nullable(),
  /** M2: an encrypted PIN is on file (the value only through the audited reveal). */
  hasPin: z.boolean(),
});
export type FleetFuelCard = z.infer<typeof FleetFuelCardSchema>;

export const DriverLicenceSchema = z.object({
  status: LicenceStatusSchema,
  lastCheckDate: IsoDate.nullable(),
  points: z.number().int().nullable(),
  relevantPoints: z.number().int().nullable(),
  band: LicenceBandSchema.nullable(),
  nextDue: IsoDate.nullable(),
  daysToDue: z.number().int().nullable(),
  disqualified: z.boolean(),
  licenceExpired: z.boolean(),
  notAuthorised: z.boolean(),
  paused: z.boolean(),
  exempt: z.boolean(),
  exemptReason: z.string().nullable(),
  licenceExpiry: IsoDate.nullable(),
});
export type DriverLicence = z.infer<typeof DriverLicenceSchema>;

export const DriverConsentSchema = z.object({
  status: ConsentStatusSchema,
  givenOn: IsoDate.nullable(),
  expiresOn: IsoDate.nullable(),
  method: z.string().nullable(),
});
export type DriverConsent = z.infer<typeof DriverConsentSchema>;

export const DriverScoreSchema = z.object({
  period: z.string(),
  score: z.number().nullable(),
  trips: z.number().int(),
  miles: z.number(),
});
export type DriverScore = z.infer<typeof DriverScoreSchema>;

// Vehicles ----------------------------------------------------------------------------

export const VehicleSummarySchema = z.object({
  id: z.uuid(),
  reg: z.string(),
  make: z.string().nullable(),
  model: z.string().nullable(),
  type: VehicleTypeSchema,
  ownership: OwnershipSchema,
  status: VehicleStatusSchema,
  vorReason: z.string().nullable(),
  isPool: z.boolean(),
  isCompanyCar: z.boolean(),
  site: SiteRefSchema.nullable(),
  driver: PersonRefSchema.nullable(),
  /** Assigned on the old system to a staff id Aroo cannot match. */
  driverUnmatched: z.boolean(),
  greyOwner: PersonRefSchema.nullable(),
  lastMileage: z.number().int().nullable(),
  lastMileageAt: Wall.nullable(),
  mileageStale: z.boolean(),
  dates: z.object({
    mot: VehicleDateCellSchema,
    tax: VehicleDateCellSchema,
    insurance: VehicleDateCellSchema,
    service: VehicleDateCellSchema,
  }),
  worstDate: VehicleDateStatusSchema,
  openRepairs: z.number().int(),
  openFines: z.number().int(),
  openIncidents: z.number().int(),
});
export type VehicleSummary = z.infer<typeof VehicleSummarySchema>;

export const VehiclesResponseSchema = z.object({
  vehicles: z.array(VehicleSummarySchema),
  sites: z.array(SiteRefSchema),
  canManage: z.boolean(),
});
export type VehiclesResponse = z.infer<typeof VehiclesResponseSchema>;

export const VehicleAssignmentSchema = z.object({
  id: z.uuid(),
  person: PersonRefSchema.nullable(),
  unmatched: z.boolean(),
  isPool: z.boolean(),
  from: Wall.nullable(),
  to: Wall.nullable(),
});
export type VehicleAssignment = z.infer<typeof VehicleAssignmentSchema>;

export const VehicleDetailSchema = z.object({
  vehicle: VehicleSummarySchema.extend({
    lessor: z.string().nullable(),
    maintenanceIncluded: z.boolean(),
    annualMileageAllowance: z.number().int().nullable(),
    trackerNo: z.string().nullable(),
    dashcam: z.string().nullable(),
    quartixVehicleId: z.number().int().nullable(),
    notes: z.string().nullable(),
    vorSince: Wall.nullable(),
    disposedOn: IsoDate.nullable(),
    disposalReason: z.enum(['sold', 'written_off', 'other']).nullable(),
    assignedSince: Wall.nullable(),
    greyBusinessCover: z.boolean(),
    legacyVehicleId: z.number().int().nullable(),
    /** M2: an encrypted ePOD password is on file for the vehicle's driver. */
    hasEpodPassword: z.boolean(),
    /** M2: an Aroo change (assignment, off road) the old system has not shown yet. */
    pendingSync: z.boolean(),
  }),
  dateHistory: z.array(VehicleDateRecordSchema),
  assignments: z.array(VehicleAssignmentSchema),
  checklists: z.array(FleetChecklistSchema),
  repairs: z.array(FleetRepairSchema),
  fines: z.array(FleetFineSchema),
  incidents: z.array(FleetIncidentSchema),
  fuelCards: z.array(FleetFuelCardSchema),
  can: z.object({
    manage: z.boolean(),
    /** M2: report a defect / incident (fleet.cases.report at its branch, or manage). */
    report: z.boolean(),
    /** M2: assign a driver, mark off road (fleet admin). */
    assign: z.boolean(),
    /** M2: reveal and set PINs / ePOD passwords (fleet admin, audited). */
    secrets: z.boolean(),
  }),
});
export type VehicleDetail = z.infer<typeof VehicleDetailSchema>;

export const VehiclePatchSchema = z
  .object({
    type: VehicleTypeSchema,
    ownership: z.enum(['owned', 'leased']),
    lessor: z.string().trim().max(80).nullable(),
    maintenanceIncluded: z.boolean(),
    isCompanyCar: z.boolean(),
    annualMileageAllowance: z.number().int().min(0).max(500_000).nullable(),
    notes: z.string().trim().max(500).nullable(),
  })
  .partial();
export type VehiclePatch = z.infer<typeof VehiclePatchSchema>;

export const VehicleDateWriteSchema = z.object({
  kind: VehicleDateKindSchema,
  dueDate: IsoDate.nullable(),
  note: z.string().trim().max(255).nullable().optional(),
});
export type VehicleDateWrite = z.infer<typeof VehicleDateWriteSchema>;

// Drivers -----------------------------------------------------------------------------

export const DriverSummarySchema = z.object({
  person: PersonRefSchema,
  site: SiteRefSchema.nullable(),
  active: z.boolean(),
  licence: DriverLicenceSchema,
  consent: DriverConsentSchema,
  score: DriverScoreSchema.nullable(),
  vehicle: VehicleRefSchema.nullable(),
  greyVehicles: z.array(VehicleRefSchema),
  openFines: z.number().int(),
  openIncidents: z.number().int(),
});
export type DriverSummary = z.infer<typeof DriverSummarySchema>;

export const DriversResponseSchema = z.object({
  drivers: z.array(DriverSummarySchema),
  sites: z.array(SiteRefSchema),
  bands: z.array(LicenceBandSchema),
});
export type DriversResponse = z.infer<typeof DriversResponseSchema>;

export const LicenceCheckRowSchema = z.object({
  id: z.number().int(),
  date: IsoDate.nullable(),
  type: z.string().nullable(),
  points: z.number().int().nullable(),
  relevantPoints: z.number().int().nullable(),
  status: z.string().nullable(),
  disqualified: z.boolean(),
});
export type LicenceCheckRow = z.infer<typeof LicenceCheckRowSchema>;

export const DriverDetailSchema = z.object({
  driver: DriverSummarySchema,
  checks: z.array(LicenceCheckRowSchema),
  scores: z.array(DriverScoreSchema),
  vehicleHistory: z.array(
    z.object({
      vehicle: VehicleRefSchema,
      isPool: z.boolean(),
      from: Wall.nullable(),
      to: Wall.nullable(),
    }),
  ),
  currentVehicle: VehicleSummarySchema.nullable(),
  greyVehicles: z.array(VehicleSummarySchema),
  fines: z.array(FleetFineSchema),
  repairs: z.array(FleetRepairSchema),
  incidents: z.array(FleetIncidentSchema),
  quartixDrivers: z.array(z.object({ driverId: z.number().int(), name: z.string().nullable() })),
  checkCost: Money,
  bands: z.array(LicenceBandSchema),
  can: z.object({ manage: z.boolean() }),
});
export type DriverDetail = z.infer<typeof DriverDetailSchema>;

/** GET /fleet/me (fleet.own.view). */
export const MyFleetSchema = z.object({
  person: PersonRefSchema,
  licence: DriverLicenceSchema,
  consent: DriverConsentSchema,
  score: DriverScoreSchema.nullable(),
  scores: z.array(DriverScoreSchema),
  vehicle: VehicleSummarySchema.nullable(),
  checklists: z.array(FleetChecklistSchema),
  greyVehicles: z.array(VehicleSummarySchema),
  /** True when Aroo knows of no licence check, consent, vehicle or score for the person. */
  empty: z.boolean(),
  /** M2: my fines and incidents (fines I can accept or dispute). */
  cases: z.array(
    z.object({
      id: z.uuid(),
      kind: z.enum(['fine', 'incident']),
      ref: z.string(),
      title: z.string(),
      status: z.string(),
      open: z.boolean(),
      reg: z.string().nullable(),
      date: IsoDate.nullable(),
      points: z.number().int().nullable(),
      /** The driver's own fine amount (they are told what they owe). */
      amount: Money.nullable(),
      needsMyResponse: z.boolean(),
    }),
  ),
  /** M2: my grey-fleet declarations still waiting (or rejected recently). */
  declarations: z.array(
    z.object({
      id: z.uuid(),
      reg: z.string(),
      status: z.enum(['pending', 'approved', 'rejected', 'withdrawn']),
      createdAt: Instant,
      decisionNote: z.string().nullable(),
    }),
  ),
  /** M2: consent captured in Aroo (re-consent, D63) is open to the person. */
  canConsent: z.boolean(),
});
export type MyFleet = z.infer<typeof MyFleetSchema>;

// Grey fleet ---------------------------------------------------------------------------

export const GreyVehicleWriteSchema = z.object({
  ownerPersonId: z.uuid(),
  reg: z.string().trim().min(2).max(16),
  make: z.string().trim().max(50).nullable().optional(),
  model: z.string().trim().max(50).nullable().optional(),
  businessCover: z.boolean(),
  insuranceExpiry: IsoDate.nullable().optional(),
  motDue: IsoDate.nullable().optional(),
  notes: z.string().trim().max(500).nullable().optional(),
});
export type GreyVehicleWrite = z.infer<typeof GreyVehicleWriteSchema>;

export const GreyVehiclePatchSchema = z
  .object({
    businessCover: z.boolean(),
    make: z.string().trim().max(50).nullable(),
    model: z.string().trim().max(50).nullable(),
    notes: z.string().trim().max(500).nullable(),
    retired: z.boolean(),
  })
  .partial();
export type GreyVehiclePatch = z.infer<typeof GreyVehiclePatchSchema>;

export const GreyFleetResponseSchema = z.object({
  vehicles: z.array(VehicleSummarySchema.extend({ greyBusinessCover: z.boolean() })),
  canManage: z.boolean(),
});
export type GreyFleetResponse = z.infer<typeof GreyFleetResponseSchema>;

// Home (exceptions queue) --------------------------------------------------------------

export const FleetExceptionKindSchema = z.enum([
  'vehicle_date_overdue',
  'vehicle_date_due',
  'vehicle_date_missing',
  'vor',
  'pool',
  'unassigned',
  'stale_mileage',
  'licence_blocked',
  'licence_overdue',
  'licence_due',
  'licence_never',
  'consent_expired',
  'consent_missing',
  'consent_expiring',
  'quartix_unlinked',
  'open_repairs',
  'open_fines',
  'open_incidents',
]);
export type FleetExceptionKind = z.infer<typeof FleetExceptionKindSchema>;

export const FleetExceptionSchema = z.object({
  key: z.string(),
  title: z.string(),
  detail: z.string().nullable(),
  vehicle: VehicleRefSchema.nullable(),
  person: PersonRefSchema.nullable(),
  site: SiteRefSchema.nullable(),
  quartixDriver: z
    .object({
      driverId: z.number().int(),
      name: z.string().nullable(),
      trips30d: z.number().int(),
      miles30d: z.number(),
    })
    .nullable(),
  /** The date that matters (due, expiry), for sorting and display. */
  date: IsoDate.nullable(),
});
export type FleetException = z.infer<typeof FleetExceptionSchema>;

export const FleetExceptionGroupSchema = z.object({
  kind: FleetExceptionKindSchema,
  title: z.string(),
  tone: z.enum(['danger', 'warn', 'info']),
  count: z.number().int(),
  items: z.array(FleetExceptionSchema),
});
export type FleetExceptionGroup = z.infer<typeof FleetExceptionGroupSchema>;

export const FleetSyncStatusSchema = z.object({
  lastOkAt: Instant.nullable(),
  stale: z.boolean(),
  failing: z.array(z.string()),
});
export type FleetSyncStatus = z.infer<typeof FleetSyncStatusSchema>;

export const FleetHomeSchema = z.object({
  groups: z.array(FleetExceptionGroupSchema),
  totals: z.object({
    vehicles: z.number().int(),
    active: z.number().int(),
    vor: z.number().int(),
    pool: z.number().int(),
    drivers: z.number().int(),
  }),
  sync: FleetSyncStatusSchema,
  can: z.object({
    manage: z.boolean(),
    vehicles: z.boolean(),
    drivers: z.boolean(),
  }),
});
export type FleetHome = z.infer<typeof FleetHomeSchema>;

export const QuartixLinkSchema = z.object({ personId: z.uuid() });
export type QuartixLink = z.infer<typeof QuartixLinkSchema>;

// Settings ---------------------------------------------------------------------------

export const FleetSettingsSchema = z.object({
  licenceBands: z.array(LicenceBandSchema),
  licenceCheckCost: Money,
  consentValidityMonths: z.number().int(),
  consentWarningDays: z.number().int(),
  licenceDueSoonDays: z.number().int(),
  vehicleReminderDays: z.array(z.number().int()),
  staleMileageDays: z.number().int(),
  escalationPerson: PersonRefSchema.nullable(),
  /** Who gets escalations when no override is set (the Ops Director seat chain). */
  escalationDefault: z.object({ people: z.array(PersonRefSchema), via: z.string() }),
  updatedAt: Instant,
});
export type FleetSettings = z.infer<typeof FleetSettingsSchema>;

export const FleetSettingsPatchSchema = z
  .object({
    licenceBands: z
      .array(LicenceBandSchema)
      .min(1)
      .max(10)
      .refine((b) => b.some((x) => x.minPoints === 0), 'One band must start at 0 points')
      .refine(
        (b) => new Set(b.map((x) => x.minPoints)).size === b.length,
        'Each band needs its own starting points',
      ),
    licenceCheckCost: z
      .string()
      .trim()
      .regex(/^\d{1,4}(\.\d{1,2})?$/, 'Use pounds and pence, e.g. 0.75'),
    consentValidityMonths: z.number().int().min(1).max(120),
    consentWarningDays: z.number().int().min(0).max(365),
    licenceDueSoonDays: z.number().int().min(0).max(180),
    vehicleReminderDays: z.array(z.number().int().min(1).max(365)).min(1).max(6),
    staleMileageDays: z.number().int().min(1).max(180),
    escalationPersonId: z.uuid().nullable(),
  })
  .partial();
export type FleetSettingsPatch = z.infer<typeof FleetSettingsPatchSchema>;

export const FleetSettingsPatchResponseSchema = z.object({
  settings: FleetSettingsSchema,
  /** Drivers whose next licence check date moved with this change. */
  nextDueMoved: z.number().int(),
});
export type FleetSettingsPatchResponse = z.infer<typeof FleetSettingsPatchResponseSchema>;

export const FleetPeopleSearchResponseSchema = z.object({
  people: z.array(PersonRefSchema.extend({ site: z.string().nullable() })),
});
export type FleetPeopleSearchResponse = z.infer<typeof FleetPeopleSearchResponseSchema>;

export const FleetIdResponseSchema = z.object({ id: z.uuid() });
export const FleetOkResponseSchema = z.object({ ok: z.literal(true) });
